import { Popup, Radio, SearchBar, Skeleton, Toast } from "antd-mobile";
import { DownOutline, RightOutline } from "antd-mobile-icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getQualityHistory,
  MobileQualityHistory,
} from "../../api/mobileQuality";
import request from "../../axios";
import { HistoryDays } from "../../utils/mobileQuality";
import styles from "./index.module.css";

interface SelectorItem {
  id: string;
  name: string;
}

const emptyHistory = (days: HistoryDays): MobileQualityHistory => ({
  days,
  startDate: dayjs().subtract(days - 1, "day").format("YYYY-MM-DD"),
  endDate: dayjs().format("YYYY-MM-DD"),
  capturedCount: 0,
  evaluatedCount: 0,
  qualifiedCount: 0,
  unqualifiedCount: 0,
  captureIssueCount: 0,
  pendingReviewCount: 0,
  qualifiedRate: 0,
  previousEvaluatedCount: 0,
  previousQualifiedRate: 0,
  qualifiedRateChange: 0,
  items: [],
});

const weekdayLabel = (date: string) => ["周日", "周一", "周二", "周三", "周四", "周五", "周六"][dayjs(date).day()];

const His = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState<HistoryDays>(7);
  const [history, setHistory] = useState<MobileQualityHistory>(() => emptyHistory(7));
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [stores, setStores] = useState<SelectorItem[]>([]);
  const [dishes, setDishes] = useState<SelectorItem[]>([]);
  const [storeId, setStoreId] = useState("");
  const [dishId, setDishId] = useState("");
  const [storePopup, setStorePopup] = useState(false);
  const [dishPopup, setDishPopup] = useState(false);
  const [dishKeyword, setDishKeyword] = useState("");

  const selectedStoreName = stores.find((item) => String(item.id) === storeId)?.name || "全部门店";
  const selectedDishName = dishes.find((item) => String(item.id) === dishId)?.name || "全部菜品";
  const filteredDishes = useMemo(
    () => dishes.filter((item) => item.name.includes(dishKeyword.trim())),
    [dishes, dishKeyword]
  );
  const dailyItems = useMemo(() => [...(history.items || [])].reverse(), [history.items]);

  useEffect(() => {
    Promise.all([
      request.get("/store/list", { pageNo: 1, pageSize: 999 }),
      request.get("/dish/list", { pageNo: 1, pageSize: 999 }),
    ]).then(([storeResponse, dishResponse]) => {
      setStores(storeResponse.data?.records || []);
      setDishes(dishResponse.data?.records || []);
    }).catch(() => Toast.show({ content: "筛选数据加载失败" }));
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadFailed(false);
    getQualityHistory({ days, storeId, dishId })
      .then((result) => {
        if (!active) return;
        if (!result) {
          setLoadFailed(true);
          return;
        }
        setHistory(result);
      })
      .catch(() => {
        if (!active) return;
        setLoadFailed(true);
        Toast.show({ content: "历史品质加载失败" });
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [days, storeId, dishId]);

  const openDay = (date: string) => {
    const params = new URLSearchParams({ date });
    if (storeId) params.set("storeId", storeId);
    if (dishId) params.set("dishId", dishId);
    navigate(`/his/detail?${params.toString()}`);
  };

  const trendText = history.previousEvaluatedCount === 0
    ? "上一周期暂无评分"
    : `${history.qualifiedRateChange >= 0 ? "+" : ""}${history.qualifiedRateChange.toFixed(1)}%`;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div><span>品质趋势</span><h1>历史记录</h1></div>
        <time>{dayjs(history.startDate).format("M月D日")}－{dayjs(history.endDate).format("M月D日")}</time>
      </header>

      <div className={styles.rangeSwitch} role="tablist" aria-label="历史范围">
        {([7, 30] as HistoryDays[]).map((value) => (
          <button
            key={value}
            className={days === value ? styles.activeRange : ""}
            onClick={() => setDays(value)}
          >近{value}天</button>
        ))}
      </div>

      <section className={styles.trendCard}>
        <div className={styles.trendTitle}><span>品质达标率</span><strong>{history.qualifiedRate.toFixed(1)}%</strong></div>
        <progress value={history.qualifiedRate} max="100">{history.qualifiedRate}%</progress>
        <div className={styles.trendFoot}>
          <span>{history.qualifiedCount} 达标 / {history.evaluatedCount} 已评分</span>
          <b className={history.qualifiedRateChange < 0 ? styles.negative : ""}>{trendText}</b>
        </div>
      </section>

      <section className={styles.filters} aria-label="历史筛选">
        <button onClick={() => setStorePopup(true)}><span>{selectedStoreName}</span><DownOutline /></button>
        <button onClick={() => setDishPopup(true)}><span>{selectedDishName}</span><DownOutline /></button>
      </section>

      <section className={styles.dailySection}>
        <div className={styles.sectionTitle}>
          <h2><i />每日概览</h2><span>点击查看记录</span>
        </div>
        {loading ? <Skeleton.Paragraph lineCount={6} animated /> : loadFailed ? (
          <div className={styles.loadError}>历史数据暂时无法加载，请稍后重试</div>
        ) : (
          <div className={styles.dailyList}>
            {dailyItems.map((day) => (
              <button key={day.date} className={styles.dailyRow} onClick={() => openDay(day.date)}>
                <span className={styles.dayDate}><strong>{dayjs(day.date).format("M月D日")}</strong><small>{weekdayLabel(day.date)}</small></span>
                <span className={styles.dayRate}>
                  <b>{day.evaluatedCount ? `${day.qualifiedRate.toFixed(1)}%` : "暂无评分"}</b>
                  <progress value={day.qualifiedRate} max="100">{day.qualifiedRate}%</progress>
                </span>
                <span className={styles.dayFailed}><b>{day.unqualifiedCount}</b><small>不达标</small></span>
                <RightOutline />
              </button>
            ))}
          </div>
        )}
      </section>

      <Popup visible={storePopup} onMaskClick={() => setStorePopup(false)} bodyClassName={styles.popup}>
        <h2>选择查看门店</h2>
        <Radio.Group value={storeId} onChange={(value) => { setStoreId(String(value)); setStorePopup(false); }}>
          <Radio className={styles.choice} value="">全部门店</Radio>
          {stores.map((item) => <Radio className={styles.choice} key={item.id} value={String(item.id)}>{item.name}</Radio>)}
        </Radio.Group>
      </Popup>

      <Popup visible={dishPopup} onMaskClick={() => setDishPopup(false)} bodyClassName={styles.popup}>
        <h2>选择菜品</h2>
        <SearchBar value={dishKeyword} onChange={setDishKeyword} placeholder="搜索菜名" />
        <Radio.Group value={dishId} onChange={(value) => { setDishId(String(value)); setDishPopup(false); }}>
          <Radio className={styles.choice} value="">全部菜品</Radio>
          {filteredDishes.map((item) => <Radio className={styles.choice} key={item.id} value={String(item.id)}>{item.name}</Radio>)}
        </Radio.Group>
      </Popup>
    </main>
  );
};

export default His;
